import React, { useState, useEffect, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import TreeView from "react-treeview";
import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Dialog,
  DialogContent,
} from "@mui/material";
import { AccountTreeOutlined, FactoryOutlined, Add } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { getFabricas } from "../../api/ProveedoresyFabricas/fabricasApi";
import { getDocumentosByFabrica } from "../../api/ProveedoresyFabricas/documentosFabricasApi";
import ProveedoresFabricasDrawer from "./ProveedoresFabricasDrawer";
import FabricasAdd from "./FabricasAdd";

/* para que salga el logueo */
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
/* para que salga el logueo */

const Fabricas = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  /*   const { isMinimized } = useContext(SidebarContext);
  const marginLeft = isMinimized ? "4em" : "15em"; */

  const { contentMargin } = useContext(SidebarContext);

  const [fabricas, setFabricas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedFabrica, setSelectedFabrica] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  const [thumbByFabrica, setThumbByFabrica] = useState({});
  const [photoDialogSrc, setPhotoDialogSrc] = useState(null);

  const filterInputSx = {
    mt: 2,
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.caja.main,
      borderRadius: 2,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        navigate("/login");
      }
    }

    fetchFabricas();
  }, [navigate]);

  const fetchFabricas = async () => {
    try {
      const data = await getFabricas();
      setFabricas(data);
      const thumbEntries = await Promise.all(
        data.map(async (fab) => {
          try {
            const docs = await getDocumentosByFabrica(fab.id_Fabrica);
            const firstImage = (docs || []).find((d) =>
              /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(d.Archivo || ""))
            );
            if (!firstImage) return [fab.id_Fabrica, null];
            return [
              fab.id_Fabrica,
              `${process.env.REACT_APP_API_URL}/uploads/fabricas/${firstImage.Archivo}`,
            ];
          } catch (_) {
            return [fab.id_Fabrica, null];
          }
        }),
      );
      setThumbByFabrica(Object.fromEntries(thumbEntries));
    } catch (error) {
      console.error("❌ Error al obtener fábricas:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Agrupar las fábricas por Tipo y País
  const groupByHierarchy = (fabricas) => {
    return fabricas.reduce((acc, fabrica) => {
      const tipo = fabrica.tipoFabrica?.NombreTipo || "UNKNOWN TYPE";
      const pais = fabrica.Pais || "UNKNOWN COUNTRY";

      if (!acc[tipo]) {
        acc[tipo] = {};
      }
      if (!acc[tipo][pais]) {
        acc[tipo][pais] = [];
      }
      acc[tipo][pais].push(fabrica);

      return acc;
    }, {});
  };

  /** Orden alfabético: tipo → país → nombre de fábrica */
  function sortGroupedFabricas(data) {
    if (!data || typeof data !== "object") return data;
    const sorted = {};
    Object.keys(data)
      .sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
      )
      .forEach((tipo) => {
        sorted[tipo] = {};
        Object.keys(data[tipo])
          .sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
          )
          .forEach((pais) => {
            const list = data[tipo][pais];
            sorted[tipo][pais] = [...list].sort((f1, f2) =>
              (f1.NombreFabrica || "").localeCompare(
                f2.NombreFabrica || "",
                undefined,
                { sensitivity: "base", numeric: true }
              )
            );
          });
      });
    return sorted;
  }

  const groupedData = sortGroupedFabricas(groupByHierarchy(fabricas));

  // ✅ Manejo de la expansión de nodos
  const toggleNode = (key) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // ✅ Filtrado de búsqueda
  const filterData = (data, searchTerm) => {
    if (!searchTerm) return data;

    const filtered = {};
    Object.keys(data).forEach((tipo) => {
      const filteredPaises = {};
      Object.keys(data[tipo]).forEach((pais) => {
        const filteredFabricas = data[tipo][pais].filter((fabrica) =>
          `${fabrica.NombreFabrica} ${tipo} ${pais}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );

        if (filteredFabricas.length > 0) {
          filteredPaises[pais] = filteredFabricas;
        }
      });

      if (Object.keys(filteredPaises).length > 0) {
        filtered[tipo] = filteredPaises;
      }
    });

    return filtered;
  };

  const filteredData = searchTerm
    ? sortGroupedFabricas(filterData(groupedData, searchTerm))
    : groupedData;

  // ✅ Abrir Drawer al hacer clic en una fábrica
  const handleSelectFabrica = (fabrica) => {
    setSelectedFabrica(fabrica);
    setDrawerOpen(true);
  };

  // ✅ Guardar cambios en la fábrica
  const handleSaveFabrica = async (updatedFabrica) => {
    await fetchFabricas(); // 🔄 Recargar datos
    setDrawerOpen(false);
  };

  // ✅ Renderización del árbol
  const renderTreeItems = () => {
    return Object.keys(filteredData).map((tipo) => (
      <Box
        key={tipo}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          padding: "1em 0.75em",
          marginBottom: 0.75,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
        }}
      >
        <TreeView
          nodeLabel={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0.5em",
                cursor: "pointer",
                fontWeight: 800,
                fontSize: ".9em",
              }}
              onClick={() => toggleNode(tipo)}
            >
              <AccountTreeOutlined
                sx={{ color: "text.secondary", marginRight: ".5em" }}
              />
              {tipo}
            </div>
          }
          collapsed={!expandedNodes[tipo]}
        >
          {Object.keys(filteredData[tipo]).map((pais) => (
            <TreeView
              key={pais}
              nodeLabel={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: "0.5em",
                    fontSize: ".8em",
                  }}
                  onClick={() => toggleNode(`${tipo}-${pais}`)}
                >
                  🌍 {pais}
                </div>
              }
              collapsed={!expandedNodes[`${tipo}-${pais}`]}
            >
              {filteredData[tipo][pais].map((fabrica) => (
                <div
                  key={fabrica.id_Fabrica}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: ".5em",
                    backgroundColor: "transparent",
                    borderRadius: "4px",
                    fontSize: ".8em",
                  }}
                  onClick={() => handleSelectFabrica(fabrica)}
                >
                  {thumbByFabrica[fabrica.id_Fabrica] ? (
                    <Box
                      component="img"
                      src={thumbByFabrica[fabrica.id_Fabrica]}
                      alt=""
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoDialogSrc(thumbByFabrica[fabrica.id_Fabrica]);
                      }}
                      sx={{
                        width: 22,
                        height: 22,
                        objectFit: "cover",
                        borderRadius: "4px",
                        marginRight: ".5em",
                        border: `1px solid ${theme.palette.divider}`,
                        cursor: "zoom-in",
                      }}
                    />
                  ) : (
                    <FactoryOutlined
                      sx={{ color: "primary.main", marginRight: ".5em" }}
                    />
                  )}
                  {fabrica.NombreFabrica}
                </div>
              ))}
            </TreeView>
          ))}
        </TreeView>
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        minHeight: "100dvh",
        boxSizing: "border-box",
        overflow: "auto",
        pt: 2,
        pr: { xs: 1.5, md: 3 },
        pb: 2,
        pl: 2,
        width: "100%",
        maxWidth: { xs: "100%", sm: 560 },
        ...contentMargin,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 9,
          background:
            "linear-gradient(135deg, rgba(0,188,212,0.12) 0%, rgba(103,58,183,0.1) 100%)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 2px 12px rgba(0,0,0,0.35)"
              : "0 2px 12px rgba(0,0,0,0.06)",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1, display: "block" }}
            >
              CRM
            </Typography>
            <Typography
              component="h1"
              variant="h5"
              sx={{ fontWeight: 800, lineHeight: 1.2 }}
            >
              {t("sidebar.factories")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, maxWidth: 420 }}
            >
              {t("FabricasPage.heroSubtitle")}
            </Typography>
          </Box>
          <Tooltip title={t("FabricasPage.addFactory")}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setAddDrawerOpen(true)}
              sx={{ border: 1, borderColor: "primary.main", flexShrink: 0 }}
            >
              <Add />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("FabricasPage.searchPlaceholder")}
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputProps={{ "aria-label": t("FabricasPage.searchPlaceholder") }}
          sx={filterInputSx}
        />
      </Paper>
      {loading ? (
        <CircularProgress sx={{ display: "block", my: 2 }} />
      ) : (
        renderTreeItems()
      )}
      {/* Drawer de edición */}
      <ProveedoresFabricasDrawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        fabrica={selectedFabrica}
        onSave={handleSaveFabrica}
      />
      {/* Drawer de añadir fábrica */}
      <FabricasAdd
        open={isAddDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        onSave={fetchFabricas}
      />
      <Dialog
        open={Boolean(photoDialogSrc)}
        onClose={() => setPhotoDialogSrc(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0,0,0,0.82)" } },
        }}
        PaperProps={{
          sx: {
            bgcolor: "rgba(18,18,18,0.98)",
            backgroundImage: "none",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <DialogContent
          sx={{
            position: "relative",
            p: { xs: 1, sm: 2 },
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setPhotoDialogSrc(null)}
            aria-label="Close photo"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              color: "common.white",
              zIndex: 1,
              bgcolor: "rgba(255,255,255,0.14)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {photoDialogSrc ? (
            <Box
              component="img"
              src={photoDialogSrc}
              alt=""
              sx={{
                maxWidth: "100%",
                maxHeight: "min(85vh, 920px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                display: "inline-block",
                mt: 0.5,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Fabricas;
